import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectAuth, updateMe } from "../features/auth/authSlice";
import { selectTheme, setTheme, THEMES } from "../features/ui/uiSlice";
import "./SettingsPage.css";

const ACCENT = "var(--c-accent)";
const BG = "var(--c-bg)";
const CARD = "var(--c-card)";
const BORDER = "var(--c-border)";
const TEXT = "var(--c-text)";
const MUTED = "var(--c-text-dim)";
const SUCCESS_BG = "var(--c-success-dim)";
const SUCCESS_COLOR = "var(--c-success)";
const ERROR_BG = "var(--c-danger-dim)";
const ERROR_COLOR = "var(--c-danger)";

function Field({ label, type = "text", value, onChange, placeholder, hint }) {
  return (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={styles.input}
        autoComplete={type === "password" ? "new-password" : "off"}
      />
      {hint && <span style={styles.hint}>{hint}</span>}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={styles.infoRow}>
      <span style={styles.infoLabel}>{label}</span>
      <span style={styles.infoValue}>{value || "—"}</span>
    </div>
  );
}

export default function SettingsPage() {
  const dispatch = useDispatch();
  const { user, status } = useSelector(selectAuth);
  const theme = useSelector(selectTheme);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");

  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [showPasswordSection, setShowPasswordSection] = useState(false);

  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || "");
      setLastName(user.last_name || "");
      setEmail(user.email || "");
    }
  }, [user]);

  const loading = status === "loading";

  const handleThemeChange = async (nextTheme) => {
    if (!nextTheme || theme === nextTheme) return;
    dispatch(setTheme(nextTheme));
    const result = await dispatch(updateMe({ theme: nextTheme }));
    if (updateMe.rejected.match(result)) {
      dispatch(setTheme(user?.theme || THEMES.DARK));
      const detail = result.payload || result.error?.message || "Ошибка сохранения темы.";
      setErrorMsg(detail);
      return;
    }
    setSuccessMsg("Тема сохранена.");
    setTimeout(() => setSuccessMsg(""), 2500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg("");
    setErrorMsg("");

    if (showPasswordSection && password && password !== passwordConfirm) {
      setErrorMsg("Пароли не совпадают.");
      return;
    }

    const payload = { first_name: firstName, last_name: lastName, email };
    if (showPasswordSection && password) {
      payload.password = password;
      payload.password_confirm = passwordConfirm;
    }

    const result = await dispatch(updateMe(payload));
    if (updateMe.fulfilled.match(result)) {
      setSuccessMsg("Профиль успешно обновлён.");
      setPassword("");
      setPasswordConfirm("");
      setShowPasswordSection(false);
      setTimeout(() => setSuccessMsg(""), 4000);
    } else {
      const detail = result.payload || result.error?.message || "Ошибка сохранения.";
      setErrorMsg(detail);
    }
  };

  const groupLabels = {
    admin: "Администратор",
    cashier: "Кассир",
    manager: "Менеджер",
    director: "Директор",
    director_assistant: "Исполнитель директора",
    warehouse_worker: "Складской работник",
    accountant: "Бухгалтер",
  };

  const userGroups = (user?.groups || [])
    .map((g) => groupLabels[g] || g)
    .join(", ");

  return (
    <div className="settings-page" style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.pageTitle}>Настройки</h2>
          <p style={styles.pageSubtitle}>Управление профилем аккаунта</p>
        </div>
      </div>

      <div className="settings-layout">
        {/* Left: Profile info card */}
        <div className="settings-side">
          <div style={styles.card}>
            <div style={styles.avatarWrap}>
              <div style={styles.avatar}>
                {(user?.first_name?.[0] || user?.username?.[0] || "?").toUpperCase()}
              </div>
            </div>
            <div style={styles.userName}>
              {[user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.username}
            </div>
            <div style={styles.userHandle}>@{user?.username}</div>
            {userGroups && (
              <div style={styles.groupBadge}>{userGroups}</div>
            )}

            <div style={styles.divider} />

            <div style={styles.infoBlock}>
              <InfoRow label="Имя пользователя" value={user?.username} />
              <InfoRow label="Email" value={user?.email} />
              <InfoRow label="Имя" value={user?.first_name} />
              <InfoRow label="Фамилия" value={user?.last_name} />
              <InfoRow label="Роли" value={userGroups} />
              {user?.is_staff && (
                <InfoRow label="Доступ" value="Администратор системы" />
              )}
            </div>
          </div>
        </div>

        {/* Right: Edit form */}
        <div className="settings-main">
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <span style={styles.cardTitle}>✏️ Редактировать профиль</span>
            </div>

            {successMsg && (
              <div style={{ ...styles.alert, background: SUCCESS_BG, color: SUCCESS_COLOR }}>
                ✓ {successMsg}
              </div>
            )}
            {errorMsg && (
              <div style={{ ...styles.alert, background: ERROR_BG, color: ERROR_COLOR }}>
                ✗ {errorMsg}
              </div>
            )}

            <form onSubmit={handleSubmit} style={styles.form}>
              <div className="settings-fields" style={styles.row}>
                <Field
                  label="Имя"
                  value={firstName}
                  onChange={setFirstName}
                  placeholder="Введите имя"
                />
                <Field
                  label="Фамилия"
                  value={lastName}
                  onChange={setLastName}
                  placeholder="Введите фамилию"
                />
              </div>

              <Field
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="example@email.com"
              />

              {/* Password section toggle */}
              <div style={styles.passwordToggleWrap}>
                <button
                  type="button"
                  style={styles.passwordToggle}
                  onClick={() => {
                    setShowPasswordSection((v) => !v);
                    setPassword("");
                    setPasswordConfirm("");
                  }}
                >
                  {showPasswordSection ? "Отмена смены пароля" : "Сменить пароль"}
                </button>
              </div>

              {showPasswordSection && (
                <div style={styles.passwordBlock}>
                  <div style={styles.passwordBlockTitle}>Новый пароль</div>
                  <Field
                    label="Новый пароль"
                    type="password"
                    value={password}
                    onChange={setPassword}
                    placeholder="Минимум 6 символов"
                    hint="Оставьте пустым, если не хотите менять пароль"
                  />
                  <Field
                    label="Подтвердите пароль"
                    type="password"
                    value={passwordConfirm}
                    onChange={setPasswordConfirm}
                    placeholder="Повторите новый пароль"
                  />
                </div>
              )}

              <div style={styles.formFooter}>
                <button
                  type="submit"
                  style={{
                    ...styles.btnPrimary,
                    opacity: loading ? 0.7 : 1,
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                  disabled={loading}
                >
                  {loading ? "Сохранение…" : "Сохранить изменения"}
                </button>
              </div>
            </form>
          </div>

          {/* System info card */}
          <div style={{ ...styles.card, marginTop: 16 }}>
            <div style={styles.cardHeader}>
              <span style={styles.cardTitle}>Информация системы</span>
            </div>
            <div style={styles.infoBlock}>
              <InfoRow label="ID пользователя" value={user?.id} />
              <InfoRow
                label="Статус"
                value={
                  <span
                    style={{
                      color: SUCCESS_COLOR,
                      fontWeight: 600,
                      fontSize: 15,
                    }}
                  >
                    ● Активен
                  </span>
                }
              />
            </div>
          </div>

          <div style={{ ...styles.card, marginTop: 16 }}>
            <div style={styles.cardHeader}>
              <span style={styles.cardTitle}>Тема интерфейса</span>
            </div>
            <div style={styles.themeRow}>
              <button
                type="button"
                style={{
                  ...styles.themeBtn,
                  ...(theme === THEMES.DARK ? styles.themeBtnActive : {}),
                }}
                onClick={() => handleThemeChange(THEMES.DARK)}
              >
                Тёмная
              </button>
              <button
                type="button"
                style={{
                  ...styles.themeBtn,
                  ...(theme === THEMES.LIGHT ? styles.themeBtnActive : {}),
                }}
                onClick={() => handleThemeChange(THEMES.LIGHT)}
              >
                Светлая
              </button>
            </div>
            <div style={styles.hintText}>
              Тема сохраняется индивидуально для каждого пользователя.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    padding: "24px 28px",
    minHeight: "100vh",
    background: BG,
    color: TEXT,
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 28,
  },
  pageTitle: {
    margin: 0,
    fontSize: 23,
    fontWeight: 800,
    color: TEXT,
    letterSpacing: "-0.3px",
  },
  pageSubtitle: {
    margin: "4px 0 0",
    fontSize: 15,
    color: MUTED,
  },
  card: {
    background: CARD,
    border: `1px solid ${BORDER}`,
    borderRadius: 16,
    padding: "20px 24px",
  },
  cardHeader: {
    marginBottom: 20,
    paddingBottom: 14,
    borderBottom: `1px solid ${BORDER}`,
  },
  cardTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: TEXT,
  },
  avatarWrap: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 12,
  },
  avatar: {
    width: 72,
    height: 72,
    borderRadius: "50%",
    background: `rgba(245,197,24,0.15)`,
    border: `2px solid ${ACCENT}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 28,
    fontWeight: 800,
    color: ACCENT,
  },
  userName: {
    textAlign: "center",
    fontSize: 18,
    fontWeight: 700,
    color: TEXT,
    marginBottom: 2,
  },
  userHandle: {
    textAlign: "center",
    fontSize: 15,
    color: MUTED,
    marginBottom: 10,
  },
  groupBadge: {
    textAlign: "center",
    display: "inline-block",
    width: "100%",
    fontSize: 14,
    fontWeight: 600,
    color: "var(--c-text-sub)",
    background: "var(--c-card-hover)",
    borderRadius: 8,
    padding: "3px 10px",
    marginBottom: 4,
    boxSizing: "border-box",
  },
  divider: {
    height: 1,
    background: BORDER,
    margin: "14px 0",
  },
  infoBlock: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    fontSize: 15,
  },
  infoLabel: {
    color: MUTED,
    flexShrink: 0,
  },
  infoValue: {
    color: TEXT,
    fontWeight: 500,
    textAlign: "right",
    overflowWrap: "anywhere",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    flex: 1,
  },
  label: {
    fontSize: 15,
    fontWeight: 600,
    color: MUTED,
    letterSpacing: "0.3px",
  },
  input: {
    background: "var(--c-input)",
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 16,
    color: TEXT,
    outline: "none",
    transition: "border-color 0.15s",
    width: "100%",
    boxSizing: "border-box",
  },
  hint: {
    fontSize: 13,
    color: MUTED,
    marginTop: 2,
  },
  passwordToggleWrap: {
    display: "flex",
    alignItems: "center",
  },
  passwordToggle: {
    background: "none",
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    color: MUTED,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    padding: "8px 14px",
    transition: "all 0.15s",
  },
  passwordBlock: {
    background: "var(--c-input)",
    border: `1px solid ${BORDER}`,
    borderRadius: 10,
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  passwordBlockTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: "var(--c-text)",
    marginBottom: 2,
  },
  formFooter: {
    display: "flex",
    justifyContent: "flex-end",
    paddingTop: 4,
  },
  btnPrimary: {
    background: "var(--c-btn)",
    color: "var(--c-btn-text)",
    border: "none",
    borderRadius: 99,
    padding: "10px 24px",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    transition: "opacity 0.15s",
  },
  alert: {
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 15,
    fontWeight: 600,
    marginBottom: 4,
  },
  themeRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  themeBtn: {
    border: `1px solid ${BORDER}`,
    borderRadius: 99,
    background: "var(--c-input)",
    color: TEXT,
    padding: "8px 12px",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  themeBtnActive: {
    borderColor: ACCENT,
    background: "var(--c-accent)",
    color: "var(--c-accent-on)",
  },
  hintText: {
    marginTop: 10,
    color: MUTED,
    fontSize: 14,
  },
};
